import { useCallback, useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";

import { AutoComplete } from "@/components/AutoCompleteInput";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useActiveOrgWorkspaces } from "@/hooks/organizations";
import { useWorkspaces } from "@/queries";
import { WorkspaceInviteFormValues } from "@/schemas/workspace-invite";

interface SelectWorkspaceProps {
  organizationId?: number;
  workspace?: { name: string; id: number } | null;
  excludeWorkspaceIds?: number[];
}

const SelectWorkspace = ({
  organizationId,
  workspace,
  excludeWorkspaceIds,
}: SelectWorkspaceProps) => {
  const form = useFormContext<WorkspaceInviteFormValues>();
  const { data: workspacesData } = useWorkspaces();
  const activeOrgWorkspaces = useActiveOrgWorkspaces();

  const workspaces = useMemo(() => {
    if (organizationId !== undefined && organizationId !== null && organizationId > 0) {
      return workspacesData?.workspaces.filter(w => w.organization_id === organizationId) ?? [];
    }
    return activeOrgWorkspaces;
  }, [organizationId, workspacesData?.workspaces, activeOrgWorkspaces]);
  const initializedRef = useRef(false);

  const watchedIds = form.watch("workspace_ids");

  const setValue = useCallback(
    (value: number[]) => {
      form.setValue("workspace_ids", value, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form.setValue],
  );

  useEffect(() => {
    if (workspace?.id && !initializedRef.current) {
      initializedRef.current = true;
      setValue([workspace.id]);
    }
  }, [workspace, setValue]);

  const excludeSet = useMemo(() => new Set(excludeWorkspaceIds ?? []), [excludeWorkspaceIds]);

  const allWorkspaces = useMemo(() => {
    const base = workspace ? [workspace, ...workspaces] : workspaces;
    if (excludeSet.size === 0) {
      return base;
    }
    return base.filter(w => !excludeSet.has(w.id));
  }, [workspace, workspaces, excludeSet]);

  const options = useMemo(
    () =>
      allWorkspaces.map(w => ({
        id: w.id.toString(),
        value: w.name,
        label: w.name,
      })),
    [allWorkspaces],
  );

  const selectedValues = useMemo(
    () => (watchedIds ?? []).map((id: number) => id.toString()),
    [watchedIds],
  );

  const handleSelectionChange = useCallback(
    (values: string[]) => {
      setValue(values.map(v => parseInt(v, 10)));
    },
    [setValue],
  );

  return (
    <FormField
      control={form.control}
      name="workspace_ids"
      render={() => (
        <FormItem className="flex flex-col">
          <FormLabel>
            Workspaces <span className="text-red-500">*</span>
          </FormLabel>
          <FormControl>
            <AutoComplete
              multi
              options={options}
              selectedValues={selectedValues}
              onSelectionChange={handleSelectionChange}
              placeholder="Search workspaces..."
              value={null}
              addNewOption={false}
              readOnly={!!workspace}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default SelectWorkspace;
